import Image from '@base/Image';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { currencySymbolMap, commasIndianFormat } from '@utils/textHelper';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import {
  getOfferMessage,
  getSkuBasedEventProps,
  getTestSeriesReferralInStorage
} from '@cont/SeeAll/utils';
import Forward from 'aquilla/icons/actions/Forward';
import IconButton from 'aquilla/core/molecules/IconButton';
import { useIsMobile } from '@stores/AppCommonData';
import { useRouter } from 'next/router';
import React from 'react';
import Skeleton from 'aquilla/core/atoms/Skeleton';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import pushRoute from '@utils/pushRoute';
import {
  CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
  CHECKOUT_STARTED
} from '@cont/Checkout/constants/segment';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { evtNameCategoryMap } from '@constants/index';
import useSkuDetails from './useSkuDetails';
import useTestSeriesInfo from './useTestSeriesInfo';

const PriceDetails = styled.div`
  width: ${({ $isSticky }) => ($isSticky ? '300px' : '534px')};
  margin-top: ${({ $isSticky }) =>
    $isSticky ? `${Spacings.SPACING_4B}` : `${Spacings.SPACING_6B}`};

  ${mediaQuery.uptoMobile} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 2;
    background-color: var(--color-base-1);
    padding: ${Spacings.SPACING_4B};
    border-top: 1px solid var(--color-divider);
  }
`;

const DiscountWrapper = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
`;

const ActionWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isSticky }) => ($isSticky ? 'column' : 'row')};
  justify-content: space-between;
  align-items: space-between;
  margin-top: ${({ $isSticky }) =>
    $isSticky ? `${Spacings.SPACING_4B}` : `${Spacings.SPACING_3B}`};

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const Price = styled.div`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.H4};
  line-height: 26px;
  color: var(--color-text-primary);
  margin-right: ${({ $isSticky }) =>
    $isSticky ? `${Spacings.SPACING_2B}` : '0'};
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isSticky }) => ($isSticky ? 'row-reverse' : 'column')};
  justify-content: ${({ $isSticky }) =>
    $isSticky ? 'flex-end' : 'space-between'};

  align-items: ${({ $isSticky }) => ($isSticky ? 'center' : 'flex-start')};

  &:has(:only-child) {
    justify-content: ${({ $isSticky }) =>
      $isSticky ? 'flex-end' : 'space-around'};
  }
`;

const Offer = styled.div`
  width: 100%;
  height: ${Spacings.SPACING_8B};
  position: relative;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const OfferImageWrapper = styled.div`
  margin-right: ${Spacings.SPACING_6};
  display: flex;
  align-items: center;
`;

const OfferText = styled.div`
  position: absolute;
  font-size: ${Fonts.P2};
  line-height: ${Spacings.SPACING_4B};
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-yellow-5);
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ButtonContainer = styled(Button)`
  width: ${({ $isSticky }) => ($isSticky ? '100%' : '214px')};
  height: ${Spacings.SPACING_12B};
  margin-top: ${({ $isSticky }) =>
    $isSticky ? `${Spacings.SPACING_4B}` : '0'};
  ${mediaQuery.uptoMobile} {
    width: 185px;
  }
`;

const ShareIconButton = styled(IconButton)`
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  margin-left: ${Spacings.SPACING_4B};
`;

const StyledShareWithTextButton = styled(Button)`
  width: 124px;
  height: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_5B};

  .button--right-icon {
    height: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 32px;
  position: relative;
  flex-shrink: 0;
`;

const StyledButtonSkeleton = styled(Skeleton)`
  &.skeleton {
    width: ${({ $isSticky }) => ($isSticky ? '100%' : '214px')};
    height: ${Spacings.SPACING_12B};
    border-radius: ${Spacings.SPACING_2B};
    margin-top: ${({ $isSticky }) =>
      $isSticky ? `${Spacings.SPACING_2B}` : 0};

    ${mediaQuery.uptoMobile} {
      width: 185px;
    }
  }
`;

const SkuPricingDetails = ({ isSticky, handleShare }) => {
  const { query } = useRouter();

  const isMobile = useIsMobile();
  const { isValidating: isMyInfoValidating } = useMyInfo();

  const { data } = useTestSeriesInfo();
  const { goal } = data;

  const { availableSku, isValidating, hasSkuAccess } = useSkuDetails(
    goal.uid,
    query.courseUID
  );

  const goalSegmentData = useGoalEventProps({ goalUID: goal.uid });

  if (isValidating || isMyInfoValidating) {
    return (
      <PriceDetails $isSticky={isSticky}>
        <ActionWrapper $isSticky={isSticky}>
          <PriceWrapper $isSticky={isSticky}>
            <div>
              <Skeleton variant="text" width={150} height={10} />
            </div>
            <div>
              <Skeleton variant="text" width={150} height={10} />
            </div>
          </PriceWrapper>
          <FlexRow>
            <StyledButtonSkeleton $isSticky={isSticky} type="rect" />
          </FlexRow>
        </ActionWrapper>
      </PriceDetails>
    );
  }

  if (isSticky && hasSkuAccess) {
    return null;
  }

  if (hasSkuAccess) {
    return (
      <StyledShareWithTextButton
        label="Share"
        variant="secondary"
        size="large"
        startIcon={<Forward />}
        onClick={handleShare}
      />
    );
  }

  const {
    price,
    currency,
    maxRetailPrice,
    discountPercentage,
    priceEffectiveTill,
    uid
  } = availableSku ?? {};
  const currencySymbol = currencySymbolMap(currency);

  const handlePurchase = () => {
    const referralCode = getTestSeriesReferralInStorage(query.courseUID);
    const referralCodeStr = referralCode
      ? `/?referral_code=${encodeURIComponent(referralCode)}`
      : '';
    pushRoute(
      `/goal/[goalSlug]/[goalUID]/subscribe/${uid}`,
      `/goal/${goal.slug}/${goal.uid}/subscribe/${uid}${referralCodeStr}`
    );
    segment.track(
      CHECKOUT_STARTED,
      {
        ...goalSegmentData,
        ...getSkuBasedEventProps(availableSku, data)
      },
      evtNameCategoryMap.BOTH
    );
    segment.track(
      CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
      {
        ...goalSegmentData,
        ...getSkuBasedEventProps(availableSku, data)
      },
      evtNameCategoryMap.BOTH
    );
  };

  const offerStripImage =
    isSticky || isMobile
      ? '/test-series-revamp/offer-strip-sticky.svg'
      : '/test-series-revamp/offer-banner.svg';

  return (
    <>
      <PriceDetails $isSticky={isSticky}>
        {priceEffectiveTill && (
          <Offer $isSticky={isSticky}>
            <ImageWrapper>
              <Image
                src={offerStripImage}
                alt="offer background"
                layout="fill"
                objectFit={isSticky ? 'fill' : 'cover'}
              />
            </ImageWrapper>

            <OfferText>
              <FlexRow>
                <OfferImageWrapper>
                  <Image
                    src="/store/offer-discount.svg"
                    height={16}
                    width={16}
                    alt="offer"
                  />
                </OfferImageWrapper>
                <div>{getOfferMessage(priceEffectiveTill)}</div>
              </FlexRow>
            </OfferText>
          </Offer>
        )}

        <ActionWrapper $isSticky={isSticky}>
          <PriceWrapper $isSticky={isSticky}>
            {maxRetailPrice ? (
              <DiscountWrapper variant="p3">
                <OriginalPrice>
                  {currencySymbol}
                  {commasIndianFormat(Math.round(maxRetailPrice))}
                </OriginalPrice>{' '}
                {discountPercentage && `(${discountPercentage}% off)`}
              </DiscountWrapper>
            ) : (
              !isSticky && <DiscountWrapper variant="p3">Price</DiscountWrapper>
            )}
            {price && (
              <Price $isSticky={isSticky}>
                {currencySymbol}
                {commasIndianFormat(Math.round(price))} only
              </Price>
            )}
          </PriceWrapper>
          <FlexRow>
            <ButtonContainer
              $isSticky={isSticky}
              label="Buy test series"
              variant="primary"
              size="large"
              onClick={handlePurchase}
            />
            {!isSticky && !isMobile && (
              <ShareIconButton variant="secondary" onClick={handleShare}>
                <Forward />
              </ShareIconButton>
            )}
          </FlexRow>
        </ActionWrapper>
      </PriceDetails>
    </>
  );
};

export default React.memo(SkuPricingDetails);
